'use client';
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Play, ArrowUpRight } from 'lucide-react';
import VideoModal from '@/components/ui/VideoModal';
import BookDemoModal from '@/components/ui/BookDemoModal';
import SplitReveal from '@/components/motion/SplitReveal';
import Eyebrow from '@/components/ui/Eyebrow';

const reveal = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } },
};

const VideoTutorialsSection = () => {
  const [activeVideo, setActiveVideo] = useState(null);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  const videos = [
    {
      id: 'ZojX5rWTajE',
      title: 'Getting Started Tutorial',
      description: 'Learn the basics of our platform',
      thumbnail: `https://img.youtube.com/vi/ZojX5rWTajE/maxresdefault.jpg`,
    },
    {
      id: 'epi7_R-jKE0',
      title: 'Advanced Features Guide',
      description: 'Explore advanced functionality',
      thumbnail: `https://img.youtube.com/vi/epi7_R-jKE0/maxresdefault.jpg`,
    },
  ];

  const openVideo = (video) => setActiveVideo(video);
  const closeVideo = () => setActiveVideo(null);

  const openContactModal = () => {
    if (typeof window !== 'undefined' && window.gtag_report_conversion) {
      window.gtag_report_conversion();
    }
    setIsContactModalOpen(true);
  };
  const closeContactModal = () => setIsContactModalOpen(false);

  return (
    <>
      <section className="relative overflow-hidden py-24 sm:py-32">
        {/* ambient glow — brand blue + mint, no purple */}
        <div className="pointer-events-none absolute -left-24 top-16 size-72 rounded-full bg-primary-blue/10 blur-3xl" />
        <div className="pointer-events-none absolute -right-20 bottom-8 size-72 rounded-full bg-emerald-200/30 blur-3xl" />

        <div className="container relative mx-auto px-5">
          {/* intro */}
          <div className="mx-auto max-w-4xl text-center">
            <Eyebrow className="mb-5">Video Tutorials</Eyebrow>
            <SplitReveal className="display text-[clamp(34px,4.4vw,64px)] text-secondary-dark">
              Learn How to Use Our Platform
            </SplitReveal>
            <p className="mx-auto mt-6 max-w-xl text-lg text-gray-500">
              Watch our comprehensive video guides to get the most out of our optical management
              system.
            </p>
          </div>

          {/* video bento */}
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: '-80px' }}
            transition={{ staggerChildren: 0.1 }}
            className="mx-auto mt-14 grid max-w-6xl grid-cols-1 gap-6 md:grid-cols-2"
          >
            {videos.map((video, index) => (
              <motion.div
                key={video.id}
                variants={reveal}
                onClick={() => openVideo(video)}
                className="group cursor-pointer"
              >
                {/* thumbnail */}
                <div className="relative aspect-video overflow-hidden rounded-2xl bg-ink shadow-[0_30px_80px_-30px_rgba(2,32,71,0.4)]">
                  <img
                    src={video.thumbnail}
                    alt={video.title}
                    className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentElement.style.background =
                        'linear-gradient(135deg, #01a7ff 0%, #0077c8 100%)';
                    }}
                  />

                  {/* play overlay */}
                  <div className="absolute inset-0 flex items-center justify-center bg-ink/30 transition-colors duration-300 group-hover:bg-ink/15">
                    <span className="grid size-20 place-items-center rounded-full bg-white shadow-[0_20px_50px_-10px_rgba(0,0,0,0.5)] transition-transform duration-300 group-hover:scale-110">
                      <Play className="size-6 translate-x-0.5 fill-primary-blue text-primary-blue" />
                    </span>
                  </div>

                  {/* tutorial badge */}
                  <span className="label absolute left-4 top-4 rounded-full bg-ink/80 px-3 py-1.5 text-[10px] text-white backdrop-blur">
                    Tutorial 0{index + 1}
                  </span>
                </div>

                {/* info */}
                <div className="flex items-start justify-between gap-4 pt-5">
                  <div>
                    <h3 className="text-2xl font-semibold tracking-tight text-secondary-dark transition-colors group-hover:text-primary-blue">
                      {video.title}
                    </h3>
                    <p className="mt-1 text-gray-500">{video.description}</p>
                  </div>
                  <span className="label mt-1.5 inline-flex shrink-0 items-center gap-1.5 text-[10px] text-primary-blue">
                    Watch
                    <ArrowUpRight
                      className="size-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                      strokeWidth={2}
                    />
                  </span>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* CTA */}
          <div className="mt-12 text-center">
            <p className="text-sm text-gray-500">
              Need more help?{' '}
              <button
                onClick={openContactModal}
                className="font-semibold text-primary-blue hover:underline"
              >
                Contact our support team
              </button>
            </p>
          </div>
        </div>
      </section>

      {/* Video Modal */}
      <VideoModal
        isOpen={!!activeVideo}
        onClose={closeVideo}
        videoId={activeVideo?.id}
        title={activeVideo?.title}
      />

      {/* Contact Modal */}
      <BookDemoModal isOpen={isContactModalOpen} onClose={closeContactModal} />
    </>
  );
};

export default VideoTutorialsSection;
